import { useAuth } from '../../contexts/AuthContext';
import { AssemblyType } from '../../enums/AssemblyType';
import { ProfileType } from '../../enums/ProfileType';
import { SeparationType } from '../../enums/SeparationType';
import { StatePrescriptionIdentification } from '../../enums/StatePrescriptionIdentification';
import { SessionHelper } from '../../helpers/SessionHelper';
import { OrderForListing } from '../../models/OrderForListing';

export interface IWidthColumnOrdersTable {
  CheckBox: string;
  OrderNumber: string;
  FulfilledBy: string;
  Alerts: string;
  PaymentAt: string;
  RecipeAt: string;
  WorkingDays: string;
  ClientDeadline: string;
  InternalDeadline: string;
  ClientName: string;
  ClientNameTitle: string;
  StateOrder: string;
  StateOrderTitle: string;
  StateDegree: string;
  StateDegreeTitle: string;
  LensMaterial: string;
  LensMaterialTitle: string;
  Options: string;
  OptionsTitle: string;
}

export interface IExtraElementsInfo {
  display?: boolean;
  displayAssemblyStatus?: boolean;
}

export function useOrdersTable() {

  const { session } = useAuth();

  function getWidthColumns(separationType?: SeparationType): IWidthColumnOrdersTable {
    const canSeeSimpleTable = SessionHelper.canSeeSimpleTable(session);
    const canSeeClientDeadlineColumns = SessionHelper.canSeeClientDeadlineColumns(session);
    const canSeeInternalDeadlineColumns = SessionHelper.canSeeInternalDeadlineColumns(session);
    const canSeeFulfilledByColumn = SessionHelper.canSeeFulfilledByColumn(session);

    if (separationType === SeparationType.Prescription) {
      if (session?.user?.profileType === ProfileType.Assembler) {
        return {
          CheckBox: '25px',
          OrderNumber: '100px',
          FulfilledBy: canSeeFulfilledByColumn ? '20px' : '0px',
          Alerts: '20px',
          PaymentAt: '125px',
          RecipeAt: '125px',
          WorkingDays: '0px',
          ClientDeadline: '0px',
          InternalDeadline: '0px',
          ClientName: '50%',
          ClientNameTitle: '48%',
          StateOrder: '0px',
          StateOrderTitle: '0px',
          StateDegree: canSeeSimpleTable ? '0px' : '30%',
          StateDegreeTitle: canSeeSimpleTable ? '0px' : '29.5%',
          LensMaterial: '0px',
          LensMaterialTitle: '0px',
          Options: canSeeSimpleTable ? '140px' : '140px',
          OptionsTitle: canSeeSimpleTable ? '85px' : '145px'
        }
      }

      return {
        CheckBox: '30px',
        OrderNumber: '100px',
        FulfilledBy: canSeeFulfilledByColumn ? '20px' : '0px',
        Alerts: '20px',
        PaymentAt: '125px',
        RecipeAt: '125px',
        WorkingDays: '0px',
        ClientDeadline: canSeeClientDeadlineColumns ? '100px' : '0px',
        InternalDeadline: canSeeInternalDeadlineColumns ? '100px' : '0px',
        ClientName: canSeeSimpleTable ? '50%' : '37%',
        ClientNameTitle: canSeeSimpleTable ? '48%' : '34%',
        StateOrder: canSeeSimpleTable ? '47.5%' : '30%',
        StateOrderTitle: canSeeSimpleTable ? '52%' : '31%',
        StateDegree: canSeeSimpleTable ? '0px' : '30%',
        StateDegreeTitle: canSeeSimpleTable ? '0px' : '30.3%',
        LensMaterial: '120px',
        LensMaterialTitle: '120px',
        Options: canSeeSimpleTable ? '140px' : '140px',
        OptionsTitle: canSeeSimpleTable ? '85px' : '145px'
      }
    }

    return {
      CheckBox: '30px',
      OrderNumber: '100px',
      FulfilledBy: canSeeFulfilledByColumn ? '20px' : '0px',
      Alerts: '20px',
      PaymentAt: '125px',
      RecipeAt: '0px',
      WorkingDays: '0px',
      ClientDeadline: canSeeClientDeadlineColumns ? '100px' : '0px',
      InternalDeadline: canSeeInternalDeadlineColumns ? '100px' : '0px',
      ClientName: canSeeSimpleTable ? '51.5%' : '51.5%',
      ClientNameTitle: canSeeSimpleTable ? '51%' : '51%',
      StateOrder: '50%',
      StateOrderTitle: '50%',
      StateDegree: '0px',
      StateDegreeTitle: '0px',
      LensMaterial: '0px',
      LensMaterialTitle: '0px',
      Options: canSeeSimpleTable ? '150px' : '140px',
      OptionsTitle: canSeeSimpleTable ? '135px' : '140px',
    }
  }

  function getVisibleColumns(columns: any): number {
    let counter = 0;
    for (var key in columns) {
      if (!key.endsWith('Title') && columns[key] !== '0px') {
        counter++;
      }
    }
    return counter;
  }

  function getExtraElementsInfo(orderForListing?: OrderForListing): IExtraElementsInfo {
    const extraElementsInfo: IExtraElementsInfo = {};

    if (!orderForListing) return extraElementsInfo;

    const firstLocationInfo = session?.locations && session?.locations?.length > 0 ? session?.locations[0] : null;
    const onlyMakeExternalAssembly = firstLocationInfo?.assemblyType === AssemblyType.External;
    const onlyMakeInternalAssemblyWithoutAssembler = firstLocationInfo?.assemblyType === AssemblyType.InternalWithoutAssembler;

    const isAssembly = (
      orderForListing?.statePrescriptionLog?.currentStateId === StatePrescriptionIdentification.AguardandoMontagemInterna ||
      orderForListing?.statePrescriptionLog?.currentStateId === StatePrescriptionIdentification.AguardandoMontagemExterna ||
      orderForListing?.statePrescriptionLog?.currentStateId === StatePrescriptionIdentification.AguardandoConferencia ||
      orderForListing?.statePrescriptionLog?.currentStateId === StatePrescriptionIdentification.Ok
    );

    if (isAssembly && (session?.user?.profileType === ProfileType.Vendor || session?.user?.profileType === ProfileType.Assembler)) {
      extraElementsInfo.displayAssemblyStatus = !onlyMakeExternalAssembly && !onlyMakeInternalAssemblyWithoutAssembler;
      extraElementsInfo.display = !onlyMakeExternalAssembly && !onlyMakeInternalAssemblyWithoutAssembler;
    }

    return extraElementsInfo;
  }

  return {
    getWidthColumns,
    getVisibleColumns,
    getExtraElementsInfo
  };
}